const BASE_FONT_SIZE = 16; // 16px = 1rem — design baseline

/** Default cap for primary content (body, headings) — scales up to 1.75× design size. */
export const FONT_SCALE_CAP_DEFAULT = 1.75;

/** Dense UI (labels, tabs, chips, metadata) — stays at design size, no scaling. */
export const FONT_SCALE_CAP_DENSE = 1;

/**
 * Unitless body1 line-height (22px / 16px). Use instead of rem/px so line height
 * tracks the element's capped font-size, not iOS root rem scaling.
 */
export const BODY1_LINE_HEIGHT = 22 / 16;

/** Unitless body2 line-height (20px / 14px). Dense metadata uses this instead of theme rem. */
export const BODY2_LINE_HEIGHT = 20 / 14;

export const TEXT_ZOOM_CSS_VAR = '--text-zoom';

/**
 * Scales a CSS length for sizing/spacing (e.g. inline icon `width: 1em`, margins);
 * Android text zoom only scales font-size. No-op on iOS/web.
 */
export const withTextZoom = (length: string): string =>
  `calc(${length} * var(${TEXT_ZOOM_CSS_VAR}, 1))`;

export type ResponsiveFontSizeOptions = {
  /** Absolute max in px; takes precedence over `maxScale`. */
  maxSizePx?: number;
  /** Max multiplier over design size. Defaults to {@link FONT_SCALE_CAP_DEFAULT}. */
  maxScale?: number;
};

const remToPx = (fontSize: string): number => {
  const [rem] = fontSize.match(/\.?\d+(\.\d+)?/) ?? ['1'];
  return Number(rem) * BASE_FONT_SIZE;
};

/**
 * Root font-size scale relative to the design baseline.
 * Useful for Storybook diagnostics; font-size rules use CSS `clamp()` instead.
 */
export const getScaleFactor = (): number => {
  const actualFontSize = parseFloat(
    window.getComputedStyle(document.documentElement).fontSize,
  );
  const scaleFactor = actualFontSize / BASE_FONT_SIZE;
  return Math.max(scaleFactor ?? 1, 1);
};

/**
 * Emits a CSS `clamp()` font-size rule. The middle value tracks root `rem`, so
 * sizing reacts to system text changes without reading scale at call time.
 *
 * @param _scaleFactor Retained for call-site compatibility; ignored by `clamp()`.
 */
export const getResponsiveFontSizeAtScale = (
  fontSize: string,
  _scaleFactor?: number,
  options: ResponsiveFontSizeOptions = {},
): string => {
  const designPx = remToPx(fontSize);
  const maxScale = options.maxScale ?? FONT_SCALE_CAP_DEFAULT;
  const maxPx = options.maxSizePx ?? designPx * maxScale;

  return `clamp(${designPx.toFixed(2)}px, ${fontSize}, ${maxPx.toFixed(2)}px)`;
};

/** Shorthand with {@link FONT_SCALE_CAP_DENSE} — stat labels, tabs, metadata, map controls. */
export const getDenseResponsiveFontSize = (
  fontSize: string,
  maxSizePx?: number,
): string =>
  getResponsiveFontSizeAtScale(fontSize, undefined, {
    maxSizePx,
    maxScale: FONT_SCALE_CAP_DENSE,
  });

/**
 * Generates a responsive CSS font-size rule using `clamp()`.
 *
 * @param fontSize Base font size in rem (e.g., `'1.25rem'`).
 * @param options Cap via `maxSizePx` and/or `maxScale` (defaults to 1.75× design size).
 */
export const getResponsiveFontSize = (
  fontSize: string,
  options?: number | ResponsiveFontSizeOptions,
): string => {
  const resolvedOptions: ResponsiveFontSizeOptions =
    typeof options === 'number' ? { maxSizePx: options } : (options ?? {});

  return getResponsiveFontSizeAtScale(fontSize, undefined, resolvedOptions);
};
